import { useEffect, useState } from 'react';
import { type FilmRequest, api, type Brand, type Card, type Stage, type Room } from '../lib/api';
import { CardDetail } from './CardDetail';
import { PlanWeek } from './PlanWeek';
import { focusMove } from '../lib/moves';
import { PlanSeries } from './PlanSeries';
import { weekLine } from '../lib/plays';
import { brief, type BriefItem, type BriefRender, type BriefPublication, type BriefLive } from '../lib/brief';

// Each stage names the tool that does its work. That hand-off is what makes the
// board a control surface rather than a status list you update after the fact.
const STAGES: { id: Stage; label: string; colour: string }[] = [
  { id: 'idea',      label: 'Idea',      colour: 'var(--ink-3)' },
  { id: 'scripted',  label: 'Scripted',  colour: 'var(--blue)' },
  { id: 'filming',   label: 'Filming',   colour: 'var(--red)' },
  { id: 'editing',   label: 'Editing',   colour: 'var(--purple)' },
  { id: 'review',    label: 'Review',    colour: 'var(--amber)' },
  { id: 'scheduled', label: 'Scheduled', colour: 'var(--green)' },
  { id: 'published', label: 'Published', colour: 'var(--teal)' },
];

export function FlightDeck({ brand, openId = null, onOpened, onFilm, onNextEpisode }: {
  brand: Brand | null;
  /** P3: film a scene — hands the card, the scene and its line to the Record screen. */
  onFilm?: (f: FilmRequest) => void;
  /** D16: the next episode from what worked — hands a theme and a subject to Hooks & SEO. */
  onNextEpisode?: (seed: { topic?: string | null; pain?: string | null }) => void;
  /** A card to open as soon as the deck shows — the one Hooks just produced. */
  openId?: string | null;
  onOpened?: () => void;
}) {
  const [cards, setCards] = useState<Card[]>([]);
  // A board with a season on it needs finding: title, theme, subject, brief.
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const shown = q ? cards.filter((c) => [c.title, c.topic, c.pain_point, c.brief].some((f) => (f ?? '').toLowerCase().includes(q))) : cards;
  // D7: the director's morning — renders and publications, for the brief.
  const [renders, setRenders] = useState<BriefRender[]>([]);
  const [pubs, setPubs] = useState<BriefPublication[]>([]);
  // D21: what the last live left behind, from the rooms' debriefs.
  const [lives, setLives] = useState<BriefLive[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [preset, setPreset] = useState('');   // E4: which of the show's presets a new card starts from
  // 190: one shot — written, arranged, dressed, covered, and landed in
  // Scripted to tweak — or step by step from Idea. A preset that says
  // one-shot ticks it; the creator can untick it.
  const [oneShot, setOneShot] = useState(false);
  const [producing, setProducing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [doorTop, setDoorTop] = useState<DoorTop | null>(null);
  useEffect(() => { if (openId) { setOpen(openId); onOpened?.(); } }, [openId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Distinguishes "no cards" from "not asked yet". Without it the first-run
  // panel flashes on every load before the request comes back, which reads as
  // the board losing your work for a moment.
  const [loaded, setLoaded] = useState(false);

  async function load() {
    try {
      setCards((await api.cards()).cards); setError(null);
      api.renders().then((r) => setRenders(r.jobs.map((j) => ({ status: j.status, card_id: j.card_id ?? null })))).catch(() => undefined);
      api.rooms().then((r) => { setRooms(r.rooms); setLives(r.rooms.filter((x) => x.debrief).map((x) => ({ id: x.id, name: x.name, debriefed_at: x.debriefed_at ?? null, followUps: x.debrief?.followUps.length ?? 0, asked: x.debrief?.asked.length ?? 0, nextTitle: x.debrief?.nextEpisode.title ?? '' }))); }).catch(() => undefined);
      api.publications().then((p) => setPubs(p.publications.map((x) => ({ status: x.status, scheduled_for: x.scheduled_for, card_title: x.card_title, platform: x.platform, published_at: x.published_at, views: x.stats?.views ?? null })))).catch(() => undefined);
    }
    catch (e) { setError((e as Error).message); }
    // Set either way: a failed load must not leave the board claiming to be
    // empty forever, and the error above is what says what went wrong.
    finally { setLoaded(true); }
  }
  useEffect(() => { void load(); }, []);

  async function move(card: Card, delta: number) {
    const i = STAGES.findIndex((s) => s.id === card.stage) + delta;
    if (i < 0 || i >= STAGES.length) return;
    const target = STAGES[i]!.id;
    setBusy(card.id);
    // Optimistic: the board should feel like moving a card, not like a form post.
    setCards((cs) => cs.map((c) => (c.id === card.id ? { ...c, stage: target } : c)));
    try { await api.moveCard(card.id, target); }
    catch (e) { setError((e as Error).message); await load(); }
    finally { setBusy(null); }
  }

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      const card = await api.createCard({ title: title.trim(), brandId: brand?.id ?? null, ...(preset ? { presetId: preset } : {}) });
      if (oneShot) {
        setProducing(true);
        try {
          const cast = Object.keys(brand?.voices ?? {}).slice(0, 5);
          await api.produce(card.id, { ...(cast.length ? { speakers: cast } : {}), approve: true, thumbnail: true, land: 'scripted' });
        } finally { setProducing(false); }
      }
      setTitle(''); setAdding(false); await load();
    } catch (err) { setError((err as Error).message); }
  }

  return (
    <>
      <div className="vhead">
        <div>
          <h2>Flight Deck</h2>
          <p>
            Every piece of content in flight. Moves are saved to the database immediately and
            scoped to this workspace.
          </p>
        </div>
        {loaded && <><DeckBrief items={brief(cards, renders, pubs, new Date(), lives)} onOpen={(id) => setOpen(id)}
                              onStartLive={async (roomId) => {
                                // D21: the next episode the debrief proposed, made here with the angle as its brief.
                                const d = rooms.find((r) => r.id === roomId)?.debrief; if (!d?.nextEpisode.title) return;
                                try {
                                  const card = await api.createCard({ title: d.nextEpisode.title, brandId: brand?.id ?? null, topic: d.nextEpisode.angle.slice(0, 200) });
                                  await api.saveBrief(card.id, [d.nextEpisode.angle, '', ...d.asked.slice(0, 3).map((q) => `- ${q}`)].join('\n')).catch(() => undefined);
                                  await load(); setOpen(card.id);
                                } catch (err) { setError((err as Error).message); }
                              }}
                              onNext={onNextEpisode && (pubs.some((p) => typeof p.views === 'number') || doorTop) ? () => {
                                // D16: the best measured card's theme and subject seed the next one —
                                // the socials' views when there are any, else the door's own plays.
                                const best = [...pubs].filter((p) => typeof p.views === 'number').sort((a, b) => (b.views ?? 0) - (a.views ?? 0))[0];
                                const card = best ? cards.find((c) => c.title === best.card_title) : undefined;
                                if (card) onNextEpisode({ topic: card.topic ?? null, pain: card.pain_point ?? null });
                                else if (doorTop) onNextEpisode({ topic: doorTop.topic, pain: doorTop.painPoint });
                              } : undefined} />
                  <DoorLine onOpen={(id) => setOpen(id)} onTop={setDoorTop} />
                  {/* The director points here when the show has slowed (slowing)
                      or is past its own usual gap (rhythm): both are answered by
                      planning, not by any one card. */}
                  <div data-move="slowing"><span data-move="rhythm" hidden /></div>
                  <PlanWeek brandId={brand?.id ?? null} onMade={async (id) => { await load(); setOpen(id); }} />
                  <PlanSeries brandId={brand?.id ?? null} onMade={async (id) => { await load(); setOpen(id); }} /></>}
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a card — title, theme, subject, brief"
               aria-label="Find a card" style={{ minWidth: 220 }} />
        {/* standout and length both end in "make the next one", which is this
            button; draft-waiting ends in reading a draft that becomes a card
            here once it is approved. */}
        <span data-move="standout" hidden /><span data-move="length" hidden />
        <span data-move="draft-waiting" hidden />
        {/* trial-start points at the same button as publish: the first move a
            new workspace is offered IS making the first episode. */}
        <button className="btn pri" data-move="publish" onClick={() => setAdding((v) => !v)}>
          <span data-move="trial-start" hidden />
          {adding ? 'Cancel' : 'New card'}
        </button>
      </div>

      {error && <div className="err">{error}</div>}

      {adding && (
        <form className="addcard" onSubmit={add}>
          <input
            autoFocus value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="Working title — e.g. Why your intake form loses half your leads"
          />
          {(brand?.presets ?? []).length > 0 && (
            <select value={preset} onChange={(e) => { setPreset(e.target.value); const p = brand?.presets?.find((x) => x.id === e.target.value); if (p?.mode) setOneShot(p.mode === 'one-shot'); }} title="E4: a named set of defaults this episode starts from">
              <option value="">the show's defaults</option>
              {(brand?.presets ?? []).map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          )}
          <label className="row" style={{ gap: 6, alignItems: 'center' }} title="190: written, arranged, dressed, covered, and landed in Scripted to tweak — or step by step from Idea">
            <input type="checkbox" checked={oneShot} onChange={(e) => setOneShot(e.target.checked)} />
            one shot
          </label>
          <button className="btn pri" type="submit" disabled={!title.trim() || producing}>{producing ? 'Producing…' : oneShot ? 'Produce' : 'Add to Idea'}</button>
        </form>
      )}

      {/*
        First run. An empty board is seven columns of dashes, which is a true
        rendering of "no cards" and tells somebody opening this for the first
        time nothing about what the product does or what to do next.

        Deliberately describes what each step PRODUCES rather than selling the
        idea of it: a person deciding whether this is worth their afternoon wants
        to know what comes out, and the honest list is short enough to read.
      */}
      {loaded && cards.length === 0 && !adding && (
        <section className="firstrun">
          <h3>Nothing in flight yet</h3>
          <p>
            A card carries one piece of content from an idea to a published video. Everything
            below happens inside this workspace — the script, the voices, the cut, the artwork.
          </p>
          <ol>
            <li><b>Write the script.</b> A rundown of speakers and lines, which you can edit by hand.</li>
            <li><b>Render.</b> Synthesised voices, drawn panels or your own footage, cut together.</li>
            <li><b>Get the artwork.</b> A thumbnail and a caption track, both built from the finished cut.</li>
            <li><b>Cut shorts.</b> Vertical excerpts, starting on a line you pick rather than a timecode.</li>
            <li><b>Publish.</b> Title, description and chapters written for the video, out to a connected channel.</li>
          </ol>
          <p className="hint">
            Two things worth doing first: set a <b>Brand kit</b> so renders carry your colours
            rather than ours, and connect a channel under <b>Connectors</b> — publishing is the
            only step that needs anything outside this workspace.
          </p>
          <button className="btn pri" onClick={() => setAdding(true)}>Start the first card</button>
        </section>
      )}

      <div className="boardscroll">
        <div className="board">
          {STAGES.map((s, si) => {
            const list = shown.filter((c) => c.stage === s.id);
            return (
              <div className="col" key={s.id} data-move={s.id === 'review' ? 'review' : undefined}>
                <div className="col-hd">
                  <span className="t"><i style={{ background: s.colour }} />{s.label}</span>
                  <span className="c">{list.length}</span>
                </div>
                {list.length === 0 && <p className="emptycol">—</p>}
                {list.map((c) => (
                  <div className={`card${open === c.id ? ' on' : ''}`} key={c.id}
                       style={{ opacity: busy === c.id ? 0.5 : 1 }}
                       /* The whole face opens the card. It used to be the title alone, and a
                          creator clicks the picture — then the pill, then the picture again —
                          and reads the board as broken. The arrows below stop the click. */
                       onClick={() => setOpen(c.id)} title="Open this card">
                    {/* updated_at is the stamp for a card whose picture is its first scene:
                        change the script and the board follows. */}
                    <CardThumb id={c.id} stamp={c.thumbnail_key ?? c.master_job_id ?? c.updated_at ?? ''} />
                    <button className="ct" onClick={(e) => { e.stopPropagation(); setOpen(c.id); }} title="Open this card">
                      {c.title}
                    </button>
                    {c.brand_name && <span className="chip">{c.brand_name}</span>}
                    {c.stage === 'published' && <span className="chip" title="Where this episode shows at rookeryos.com">{c.visibility === 'members' ? 'members only' : c.visibility === 'unlisted' ? 'unlisted' : 'on the door'}</span>}
                    <div className="row">
                      <button className="mv" disabled={si === 0 || busy === c.id}
                              onClick={(e) => { e.stopPropagation(); void move(c, -1); }} aria-label="Move back">←</button>
                      <button className="mv" disabled={si === STAGES.length - 1 || busy === c.id}
                              onClick={(e) => { e.stopPropagation(); void move(c, 1); }} aria-label="Move forward">→</button>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {open && (
        <CardDetail
          id={open}
          onFilm={onFilm}
          onClose={() => { setOpen(null); void load(); }}
          onChanged={(stage) =>
            // Keep the board in step with work done in the drawer, without a
            // full reload on every poll — the drawer already polls a render.
            setCards((cs) => cs.map((c) => (c.id === open ? { ...c, stage } : c)))
          }
        />
      )}
    </>
  );
}

/**
 * The card's picture on the board.
 *
 * It used to ask only when the card had a chosen thumbnail or a render, which
 * meant an episode with ten scenes of footage sat here as a bare title until
 * it had been cut. The route now falls back to the first scene's own still, so
 * the ask is always worth making; a card with nothing to show still 404s and
 * still draws nothing. The stamp only busts the cache when the picture moves.
 */
function CardThumb({ id, stamp }: { id: string; stamp: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setUrl(null);
    api.cardThumbnail(id).then((u) => { if (live) setUrl(u); }).catch(() => undefined);
    return () => { live = false; };
  }, [id, stamp]);
  if (!url) return null;
  return <img className="cthumb" src={url} alt="" />;
}

/** D7: the board's one line — what is in flight, what failed, what is next. */
function DeckBrief({ items, onOpen, onNext, onStartLive }: { items: BriefItem[]; onOpen: (id: string) => void; onNext?: () => void; onStartLive?: (roomId: string) => void }) {
  return (
    <div className="deck-brief" data-tool="brief" aria-label="The director's morning">
      {onNext && (
        <button type="button" className="chip brief-next" title="Start the next episode from the theme and subject of the one that did best" onClick={onNext}>
          Next episode →
        </button>
      )}
      {items.map((it, i) => (
        <span key={i} className={`chip brief-${it.kind}`}
              role={it.cardId ? 'button' : undefined} tabIndex={it.cardId ? 0 : undefined}
              onClick={() => it.cardId && onOpen(it.cardId)}
              onKeyDown={(e) => { if (it.cardId && (e.key === 'Enter' || e.key === ' ')) onOpen(it.cardId); }}
              title={it.cardId ? 'Open the card' : undefined}>
          {it.text}
          {it.kind === 'live' && it.roomId && onStartLive && <button type="button" className="btn tiny pri" data-tool="brief-start-live" style={{ marginLeft: 8 }} onClick={(e) => { e.stopPropagation(); onStartLive(it.roomId!); }}>Start it</button>}
        </span>
      ))}
    </div>
  );
}

/** The door's line on the deck: what the audience did, and what it asked. */
type DoorTop = { cardId: string; title: string; topic: string | null; painPoint: string | null; plays: number };

function DoorLine({ onOpen, onTop }: { onOpen: (cardId: string) => void; onTop?: (top: DoorTop | null) => void }) {
  const [line, setLine] = useState<string | null>(null);
  const [moves, setMoves] = useState<{ cardId: string; title: string; count: number }[]>([]);
  const [top, setTop] = useState<DoorTop[]>([]);
  useEffect(() => {
    Promise.all([api.doorStatus(), api.doorQuestions().catch(() => [])]).then(([d, qs]) => {
      if (d.episodes.published === 0) { setLine(null); return; }
      const waiting = qs.filter((q) => !q.answerable).length;
      setMoves(d.unanswered ?? []);
      const tops = (d.topByPlays ?? []).filter((t) => t.plays > 0);
      setTop(tops); onTop?.(tops[0] ?? null);
      const wk = weekLine(d.playsWeek ?? 0, d.playsPrevWeek ?? 0);
      const rem = d.reminders ? ` · ${d.reminders} waiting for your next live` : '';
      setLine(`On the door: ${d.plays.toLocaleString()} plays${wk ? ` (${wk})` : ''} · ${d.followers.toLocaleString()} followers · ${d.episodes.public} public episode${d.episodes.public === 1 ? '' : 's'}${waiting ? ` · ${waiting} question${waiting === 1 ? '' : 's'} nobody has answered yet` : ''}${d.flags.review ? ` · ${d.flags.review} under review` : ''}${rem}`);
    }).catch(() => setLine(null));
  }, []);
  if (!line) return null;
  return (
    <div data-tool="door-line" style={{ margin: '4px 0 8px' }}>
      <p className="sub-dim" style={{ margin: 0 }}>{line} <a href="https://rookeryos.com/" target="_blank" rel="noreferrer">rookeryos.com ↗</a></p>
      {top.length > 0 && (
        <p className="sub-dim" style={{ margin: '2px 0 0' }} data-tool="door-top">Most played this month: {top.map((t, i) => <span key={t.cardId}>{i > 0 ? ' · ' : ''}<button type="button" className="linklike" onClick={() => onOpen(t.cardId)}>{t.title.length > 40 ? t.title.slice(0, 39) + '…' : t.title}</button> {t.plays.toLocaleString()}</span>)}</p>
      )}
      {moves.length > 0 && (
        // data-move: the director's rail points here for 'answer'.
        <div data-move="answer" className="row wrap" style={{ gap: 6, marginTop: 4 }}>
          {moves.map((m) => <button key={m.cardId} type="button" className="chip brief-next" title="Open the card and answer" onClick={() => { onOpen(m.cardId); focusMove('answer-draft'); }}>Answer {m.count} comment{m.count === 1 ? '' : 's'} on “{m.title.length > 40 ? m.title.slice(0, 37) + '…' : m.title}”</button>)}
        </div>
      )}
    </div>
  );
}
